import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { useClients } from '../clients/useData';

// Types
export interface ClientVaultFolderTemplate {
  id: number;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientVaultFolder {
  id: number;
  clientId: number;
  templateId: number;
  template: ClientVaultFolderTemplate;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: {
    records: number;
    files: number;
  };
}

export interface ClientVaultRecord {
  id: number;
  folderId: number;
  title: string;
  recordType: string;
  data: any;
  encryptedData?: string;
  tags: string[];
  isPrivate: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientVaultFile {
  id: number;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  description?: string;
  isPrivate: boolean;
}

export interface ClientVault {
  clientId: number;
  folders: (ClientVaultFolder & {
    records: ClientVaultRecord[];
    files: ClientVaultFile[];
  })[];
}

// Hooks
export const useClientVault = (clientId: number, includePrivate: boolean = false) => {
  const queryString = includePrivate ? '?includePrivate=true' : '';
  const { data, error, isLoading, mutate } = useSWR<ClientVault>(
    clientId ? `/client-vault/${clientId}/vault${queryString}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data,
    vault: data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClientVaultFolders = (clientId: number) => {
  const { data, error, isLoading, mutate } = useSWR<ClientVaultFolder[]>(
    clientId ? `/client-vault/${clientId}/folders` : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    folders: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClientVaultFolder = (clientId: number, folderId: number) => {
  const { data, error, isLoading, mutate } = useSWR<ClientVaultFolder>(
    clientId && folderId ? `/client-vault/${clientId}/folders/${folderId}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    folder: data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClientVaultRecords = (clientId: number, folderId: number, includePrivate: boolean = false) => {
  const queryString = includePrivate ? '?includePrivate=true' : '';
  const { data, error, isLoading, mutate } = useSWR<ClientVaultRecord[]>(
    clientId && folderId ? `/client-vault/${clientId}/folders/${folderId}/records${queryString}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    records: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClientVaultFiles = (clientId: number, folderId: number, includePrivate: boolean = false) => {
  const queryString = includePrivate ? '?includePrivate=true' : '';
  const { data, error, isLoading, mutate } = useSWR<ClientVaultFile[]>(
    clientId && folderId ? `/client-vault/${clientId}/folders/${folderId}/files${queryString}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    files: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClientVaultSearch = (clientId: number, query: string, includePrivate: boolean = false) => {
  const queryString = `?q=${encodeURIComponent(query)}${includePrivate ? '&includePrivate=true' : ''}`;
  const { data, error, isLoading, mutate } = useSWR<{
    records: (ClientVaultRecord & { folder: { id: number; template: ClientVaultFolderTemplate } })[];
    files: (ClientVaultFile & { folder: { id: number; template: ClientVaultFolderTemplate } })[];
  }>(
    clientId && query ? `/client-vault/${clientId}/search${queryString}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    results: data,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClientVaultTemplates = (includeInactive: boolean = false) => {
  const queryString = includeInactive ? '?includeInactive=true' : '';
  const { data, error, isLoading, mutate } = useSWR<ClientVaultFolderTemplate[]>(
    `/client-vault/templates${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    templates: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

